/* ==========================================================================
   AZURO — deferred video in the product description
   --------------------------------------------------------------------------
   The product description embeds a strip of looping clips. main-product.liquid
   has already renamed their `autoplay` to `data-az-autoplay` and forced
   `preload="none"` on the way out of Liquid, so nothing downloads with the
   page. This file gives playback back.

   THE WHOLE POINT IS THAT NOBODY NOTICES. A visitor scrolling quickly must
   find the clips already running, never a grey card catching up. That is a
   question of how far ahead we start, so there are two stages:

     WARM  — two viewport heights out. preload flips to 'auto' and load() is
             called. The browser fetches, and paints the first frame as soon
             as it has one, so the card is never empty by the time it arrives.
     PLAY  — a third of a viewport out, comfortably before the card is really
             on screen. play() is called on something already buffered, so it
             starts on the spot.

   Leaving the viewport pauses rather than resets: currentTime is kept, so
   scrolling back finds the clip where it was, the way a real autoplaying
   loop behaves. Nothing ever rewinds under the visitor.

   FAIL-SAFE. No IntersectionObserver, or a play() the browser refuses: every
   clip is started immediately and unconditionally, which is exactly the
   behaviour the description asked for in the first place. The worst case is
   the old behaviour, never a dead card.
   ========================================================================== */

(function () {
  'use strict';

  var SELECTOR = 'video[data-az-autoplay]';

  // Two viewport heights of runway. On a slow connection a 450 kB clip needs
  // roughly two seconds, and two screens of scrolling takes longer than that
  // at any speed a person actually reads at.
  //
  // On this product page the strip sits just under the fold, so this margin
  // covers it from the first paint — buffering would start immediately and we
  // would be back where we started, 37 MB racing the LCP image. Hence
  // whenIdle() below: the runway is generous, but the clock only starts once
  // the page itself is done loading.
  var WARM_MARGIN = '200% 0px';
  var PLAY_MARGIN = '35% 0px';

  // How long to wait for one clip before starting the next. Buffering all of
  // them at once splits the connection five ways and the first one — the only
  // one anybody sees first — arrives last.
  var WARM_STEP_TIMEOUT = 2500;

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function play(video) {
    // A muted inline video is allowed to autoplay everywhere, but play() still
    // returns a promise that can reject — a tab in the background, a codec the
    // device will not decode. Swallow it: a still first frame is a fine
    // outcome, an unhandled rejection in the console is not.
    var attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') attempt.catch(function () {});
  }

  function warm(video) {
    if (video.dataset.azWarm === 'true') return;
    video.dataset.azWarm = 'true';
    video.preload = 'auto';
    // load() is what actually starts the fetch after preload="none" — setting
    // the property alone does nothing to a media element already parsed.
    try { video.load(); } catch (error) { /* nothing worth reporting */ }
  }

  // Nothing this file does is worth a single millisecond of the page's own
  // load. Everything below waits for it.
  function whenIdle(run) {
    var go = function () {
      if (window.requestIdleCallback) window.requestIdleCallback(run, { timeout: 2000 });
      else setTimeout(run, 200);
    };
    if (document.readyState === 'complete') go();
    else window.addEventListener('load', go, { once: true });
  }

  // Warms one clip at a time, in the order they appear, moving on as soon as
  // each has enough to play through — or after WARM_STEP_TIMEOUT, so a clip
  // that stalls never blocks the rest of the queue.
  function warmInOrder(videos) {
    var i = 0;

    var next = function () {
      if (i >= videos.length) return;
      var video = videos[i++];

      if (video.dataset.azWarm === 'true') return next();

      var advanced = false;
      var advance = function () {
        if (advanced) return;
        advanced = true;
        video.removeEventListener('canplaythrough', advance);
        clearTimeout(timer);
        next();
      };

      var timer = setTimeout(advance, WARM_STEP_TIMEOUT);
      video.addEventListener('canplaythrough', advance);
      warm(video);
    };

    next();
  }

  function startEverything(videos) {
    Array.prototype.forEach.call(videos, function (video) {
      warm(video);
      play(video);
    });
  }

  function init(root) {
    var videos = (root || document).querySelectorAll(SELECTOR);
    if (!videos.length) return;

    // Without IntersectionObserver there is no safe way to know what is on
    // screen, so honour the description as written and play them all.
    if (!('IntersectionObserver' in window)) {
      startEverything(videos);
      return;
    }

    var playObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          if (entry.isIntersecting) {
            // Landing straight onto the strip — a deep link, a fast flick —
            // skips the warm stage entirely, so make sure it has been asked
            // for before asking it to play.
            warm(video);
            if (!prefersReducedMotion.matches) play(video);
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      { rootMargin: PLAY_MARGIN }
    );

    var fresh = [];
    Array.prototype.forEach.call(videos, function (video) {
      if (video.dataset.azVideoReady === 'true') return;
      video.dataset.azVideoReady = 'true';

      // The attribute was renamed in Liquid purely to stop the browser acting
      // on it. Everything else about the clip — muted, loop, playsinline —
      // is still what the description asked for.
      video.muted = true;
      video.setAttribute('muted', '');
      video.playsInline = true;

      fresh.push(video);
    });
    if (!fresh.length) return;

    // BOTH OBSERVERS WAIT FOR LOAD, AND THE REASON IS GEOMETRY.
    //
    // Attaching the play observer at DOMContentLoaded looks harmless and is
    // not. At that moment the page is still short: the bundle app has not yet
    // inserted its ~420px block, and the description sits far higher than it
    // will end up. Measured here, the strip read as one screen down at 750ms
    // and 3.8 screens down once everything had settled — so the observer saw
    // it as good as visible, played all five, and pulled 37 MB straight into
    // the LCP race. Exactly what this file exists to prevent.
    //
    // Waiting for load costs about 200ms of responsiveness, during which
    // nobody has scrolled four screens, and buys a layout that is telling the
    // truth.
    whenIdle(function () {
      fresh.forEach(function (video) { playObserver.observe(video); });

      var starter = new IntersectionObserver(
        function (entries) {
          var near = entries.some(function (entry) { return entry.isIntersecting; });
          if (!near) return;
          starter.disconnect();
          warmInOrder(fresh);
        },
        { rootMargin: WARM_MARGIN }
      );
      fresh.forEach(function (video) { starter.observe(video); });
    });

    // A visitor who turns motion down mid-visit should see the clips settle,
    // and one who turns it back up should see them resume.
    var onMotionChange = function () {
      Array.prototype.forEach.call(document.querySelectorAll(SELECTOR), function (video) {
        if (prefersReducedMotion.matches) video.pause();
      });
    };
    if (prefersReducedMotion.addEventListener) prefersReducedMotion.addEventListener('change', onMotionChange);
    else if (prefersReducedMotion.addListener) prefersReducedMotion.addListener(onMotionChange);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
